/**
 * Segments Management Page
 *
 * Allows admins to create, edit, and manage contact segments.
 * Supports both dynamic (query-based) and static (manual) segments.
 */

import { useState, useEffect, useMemo, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  getSegments,
  getSegmentById,
  createSegment,
  updateSegment,
  deleteSegment,
  addContactsToSegment,
} from "../services/segments.service";
import {
  refreshListCount,
  downloadSegmentCsv,
  SegmentCsvExportError,
  getRecentStaticListMembers,
  unarchiveSegment,
  type StaticListRecentMember,
} from "../services/lists.service";
import { formatRelativeDateTime } from "../utils/formatters";
import {
  EXPORTABLE_FIELDS,
  DEFAULT_EXPORT_FIELD_KEYS,
} from "../utils/segmentCsv";
import { Link, useSearchParams } from "react-router-dom";
import type {
  Segment,
  CreateSegmentInput,
  UpdateSegmentInput,
} from "../types/segment.types";
import type {
  QueryCriteria,
  QueryGroup,
  QueryCondition,
} from "../types/list.types";
import type { UUID } from "../types/common.types";
import ListFormModal from "../components/lists/ListFormModal";
import ContactSearchModal from "../components/lists/ContactSearchModal";
import CombineSegmentsModal from "../components/campaigns/CombineSegmentsModal";
import SegmentImportWizard from "../components/segments/SegmentImportWizard";
import SegmentImportHistory from "../components/segments/SegmentImportHistory";
import { useToast } from "../components/ui/Toast";

// Roles allowed to recompute a segment's count. Mirrors the role gate
// inside refresh_list_count() so the button is hidden for users whose
// click would 401 anyway. RPC enforces independently as defense in depth.
const REFRESH_ELIGIBLE_ROLES = new Set([
  "admin",
  "superadmin",
  "super_admin",
  "manager",
]);

// unarchive_segment (migration 1470) allows these roles; the button follows it.
const UNARCHIVE_ROLES = new Set([
  "admin",
  "superadmin",
  "super_admin",
  "manager",
  "staff",
  "it",
]);

/** Badge text for an archived segment (migration 1470), or null when live. */
function archiveLabel(segment: Segment): string | null {
  if (!segment.archived_at) {
    return null;
  }
  switch (segment.archive_state) {
    case "offloading":
      return "Archived · moving to archive";
    case "offloaded":
      return "Archived · members in archive";
    case "restoring":
      return "Restoring…";
    default:
      return "Archived";
  }
}

function isQueryGroup(node: QueryCondition | QueryGroup): node is QueryGroup {
  return (node as QueryGroup).conditions !== undefined;
}

/**
 * Render a count's freshness indicator: "Just now" / "5 min ago" /
 * "2 days ago" / "Never calculated". Cap at 30 days for readability —
 * anything older just shows the date.
 */
function formatLastCalculated(ts: string | null | undefined): string {
  if (!ts) {
    return "Never calculated";
  }
  const then = new Date(ts).getTime();
  const now = Date.now();
  const diffMs = now - then;
  if (diffMs < 0) {
    return "Just now";
  }
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) {
    return "Just now";
  }
  const min = Math.floor(sec / 60);
  if (min < 60) {
    return `${min} min ago`;
  }
  const hr = Math.floor(min / 60);
  if (hr < 24) {
    return `${hr} hr ago`;
  }
  const day = Math.floor(hr / 24);
  if (day < 30) {
    return `${day} day${day === 1 ? "" : "s"} ago`;
  }
  return new Date(ts).toLocaleDateString();
}

function summariseCondition(c: QueryCondition): string {
  const v = c.value;
  let valueStr: string;
  if (Array.isArray(v)) {
    valueStr = `[${v.join(", ")}]`;
  } else if (v === null || v === undefined || v === "") {
    valueStr = "—";
  } else {
    valueStr = String(v);
  }
  // Replace operator underscores so "not_equals" reads as "not equals" in the summary.
  const op = String(c.operator).replace(/_/g, " ");
  return `${c.field} ${op} ${valueStr}`;
}

function summariseCriteria(criteria: QueryCriteria | null | undefined): string {
  if (!criteria || !criteria.conditions || criteria.conditions.length === 0) {
    return "(no criteria — all contacts match)";
  }
  const conjunction = (criteria.operator || "AND").toUpperCase();
  const parts = criteria.conditions.map((c) =>
    isQueryGroup(c)
      ? `(${summariseCriteria(c)})`
      : summariseCondition(c as QueryCondition),
  );
  return parts.join(` ${conjunction} `);
}

/**
 * Small monospace + copy-icon pill for displaying a segment's UUID. Used
 * under the segment card name (list view) and in the sidebar header. The
 * UUID itself is the integration handle; partner integrations use it via
 * the prospect-add-to-segment endpoint, so making it visible + copyable
 * removes the operator's "go look up the id in the URL" step.
 */
const SegmentIdPill: React.FC<{ id: string }> = ({ id }) => {
  return (
    <div className="flex items-center gap-1 text-[10px] text-gray-400 font-mono">
      <span className="select-all">{id}</span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          void navigator.clipboard.writeText(id);
        }}
        className="text-gray-400 hover:text-gray-700"
        title="Copy ID"
        aria-label="Copy segment ID"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-3 h-3"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m9 6.625h-9.375a1.125 1.125 0 01-1.125-1.125V7.875"
          />
        </svg>
      </button>
    </div>
  );
};

type SegmentSortColumn =
  | "name"
  | "created_at"
  | "updated_at"
  | "contact_count"
  | "last_calculated";

// Stable empty array so ContactSearchModal's exclusion prop keeps a constant
// identity across renders (an inline `[]` would re-fire its search effect).
const EMPTY_CONTACT_IDS: UUID[] = [];

/**
 * Build a safe, actionable download-failure message. Server-produced messages are already opaque
 * (the edge function never returns raw SQL/PostgREST text); anything else is replaced with a
 * generic string so a PostgREST error cannot reach the UI.
 */
export function segmentCsvErrorMessage(error: unknown): string {
  if (error instanceof SegmentCsvExportError) {
    const ref = error.correlationId
      ? ` Reference: ${error.correlationId}.`
      : "";
    if (error.status === 403) {
      return `You do not have permission to export this segment.${ref}`;
    }
    if (error.status === 404) {
      return `That segment no longer exists — refresh the page.${ref}`;
    }
    return `${error.message}${ref}`;
  }
  return "CSV download failed. Please retry; if it keeps failing, contact support.";
}

export default function Segments() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const [segments, setSegments] = useState<Segment[]>([]);
  // Selected segment is just the metadata row. No sample-contacts fetch
  // happens on click — operators wanting to see the contacts use
  // Download CSV. Counts come from the cached `lists.contact_count`
  // column populated by the explicit Refresh action (or the count
  // computed at create time for new dynamic segments).
  const [selectedSegment, setSelectedSegment] = useState<Segment | null>(null);

  // Event Campaigns links here with the source segment id. Select it ONCE as soon
  // as the list loads so "View" opens the exact EIB audience rather than dropping
  // the operator at the top of a large segment index. Applied via a ref so a later
  // manual click on a DIFFERENT segment isn't snapped back to the deep-linked one
  // while ?segment= remains in the URL.
  // Waits for the FIRST completed index load (success or failure); if the id is
  // not in the loaded index it is fetched directly, so the deep link never
  // silently does nothing (the index used to stop at PostgREST's 1000-row cap).
  const deepLinkApplied = useRef(false);
  const [segmentsLoadedOnce, setSegmentsLoadedOnce] = useState(false);
  useEffect(() => {
    if (deepLinkApplied.current) {
      return;
    }
    const requestedId = searchParams.get("segment");
    if (!requestedId || !segmentsLoadedOnce) {
      return;
    }
    deepLinkApplied.current = true;
    const requested = segments.find((segment) => segment.id === requestedId);
    if (requested) {
      setSelectedSegment(requested);
      return;
    }
    getSegmentById(requestedId)
      .then((segment) => {
        if (segment) {
          // Never override a segment the operator clicked in the meantime.
          setSelectedSegment((current) => current ?? segment);
        }
      })
      .catch((err) => {
        console.warn("Deep-linked segment could not be loaded:", err);
      });
  }, [searchParams, segments, segmentsLoadedOnce]);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [csvProgress, setCsvProgress] = useState<{
    loaded: number;
    total: number;
  } | null>(null);
  // Selectable export fields (Feature B). Persisted to localStorage only — no DB. The order of this
  // array is the CSV column order (a field is appended when checked). Invalid keys are ignored by
  // the service whitelist regardless.
  const EXPORT_FIELDS_STORAGE_KEY = "segmentExportFields";
  const [showFieldPicker, setShowFieldPicker] = useState(false);
  const [exportFields, setExportFields] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(EXPORT_FIELDS_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const valid = parsed.filter(
            (k): k is string =>
              typeof k === "string" &&
              EXPORTABLE_FIELDS.some((f) => f.key === k),
          );
          if (valid.length > 0) {
            return valid;
          }
        }
      }
    } catch {
      /* ignore malformed storage */
    }
    return [...DEFAULT_EXPORT_FIELD_KEYS];
  });

  const persistExportFields = (fields: string[]) => {
    setExportFields(fields);
    try {
      localStorage.setItem(EXPORT_FIELDS_STORAGE_KEY, JSON.stringify(fields));
    } catch {
      /* storage may be unavailable; selection still applies this session */
    }
  };

  const toggleExportField = (key: string) => {
    persistExportFields(
      exportFields.includes(key)
        ? exportFields.filter((k) => k !== key)
        : [...exportFields, key],
    );
  };
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCombineModal, setShowCombineModal] = useState(false);
  const [editingSegment, setEditingSegment] = useState<Segment | null>(null);
  const [showAddContactsModal, setShowAddContactsModal] = useState(false);
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [showAppendWizard, setShowAppendWizard] = useState(false);
  // Bumped on wizard success so the import-history panel remounts and refetches.
  const [importHistoryKey, setImportHistoryKey] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<SegmentSortColumn>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [refreshingId, setRefreshingId] = useState<UUID | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [unarchivingId, setUnarchivingId] = useState<UUID | null>(null);

  // Last-5-added panel state for the currently selected static segment.
  // Operators use this to confirm test accounts landed and to spot-check
  // inbound form/API traffic. Re-fetched on selection change and after
  // a successful Add Contacts action. Dynamic segments skip the fetch.
  // recentMembersReloadKey is bumped after Add Contacts to force a
  // re-fetch via the effect below (without it, we'd need a fire-and-forget
  // call that races against quick segment-switching).
  const [recentMembers, setRecentMembers] = useState<StaticListRecentMember[]>(
    [],
  );
  const [recentMembersLoading, setRecentMembersLoading] = useState(false);
  const [recentMembersError, setRecentMembersError] = useState<string | null>(
    null,
  );
  const [recentMembersReloadKey, setRecentMembersReloadKey] = useState(0);

  const userRole = user?.role?.toLowerCase() ?? "";
  const canRefresh = REFRESH_ELIGIBLE_ROLES.has(userRole);
  const canUnarchive = UNARCHIVE_ROLES.has(userRole);
  const archivedCount = useMemo(
    () => segments.filter((s) => !!s.archived_at).length,
    [segments],
  );

  const filteredSegments = useMemo(() => {
    // Archived segments are hidden unless "Show archived" is ticked.
    let result = showArchived
      ? [...segments]
      : segments.filter((s) => !s.archived_at);

    // Filter by search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          (s.description && s.description.toLowerCase().includes(q)),
      );
    }

    // Sort
    result.sort((a, b) => {
      let cmp: number;
      switch (sortBy) {
        case "name":
          cmp = a.name.localeCompare(b.name, undefined, {
            sensitivity: "base",
          });
          break;
        case "contact_count":
          cmp = (a.contact_count ?? 0) - (b.contact_count ?? 0);
          break;
        case "last_calculated": {
          // Treat "never calculated" as the oldest possible value. In
          // ascending order, those rows surface FIRST — which matches
          // the operator workflow "find segments that need attention".
          // In descending order they're last, behind freshly-refreshed
          // rows.
          const at = a.last_calculated
            ? new Date(a.last_calculated).getTime()
            : -Infinity;
          const bt = b.last_calculated
            ? new Date(b.last_calculated).getTime()
            : -Infinity;
          cmp = at - bt;
          break;
        }
        case "updated_at":
          cmp =
            new Date(a.updated_at ?? a.created_at).getTime() -
            new Date(b.updated_at ?? b.created_at).getTime();
          break;
        case "created_at":
        default:
          cmp =
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
      }
      return sortOrder === "asc" ? cmp : -cmp;
    });

    return result;
  }, [segments, searchQuery, sortBy, sortOrder, showArchived]);

  const toggleSort = (column: SegmentSortColumn) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      // Default direction depends on the column: name ascending feels
      // natural; size/recency make more sense descending.
      setSortOrder(column === "name" ? "asc" : "desc");
    }
  };

  useEffect(() => {
    loadSegments();
  }, []);

  // Recent-members panel: race-safe fetch driven by selection + reload key.
  // Switching segments quickly previously had a window where a stale fetch
  // could land on the new selection — fixed by the `cancelled` flag in the
  // effect's cleanup. Add Contacts forces a re-fetch by bumping
  // recentMembersReloadKey, which the effect depends on.
  useEffect(() => {
    if (!selectedSegment) {
      setRecentMembers([]);
      setRecentMembersError(null);
      setRecentMembersLoading(false);
      return;
    }
    if (selectedSegment.is_dynamic) {
      setRecentMembers([]);
      setRecentMembersError(null);
      setRecentMembersLoading(false);
      return;
    }

    let cancelled = false;
    setRecentMembersLoading(true);
    setRecentMembersError(null);

    void (async () => {
      try {
        const rows = await getRecentStaticListMembers(selectedSegment.id, 5);
        if (cancelled) {
          return;
        }
        setRecentMembers(rows);
      } catch (err) {
        if (cancelled) {
          return;
        }
        console.error("Error fetching recent segment members:", err);
        setRecentMembersError("Couldn't load recent additions");
        setRecentMembers([]);
      } finally {
        if (!cancelled) {
          setRecentMembersLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    selectedSegment?.id,
    selectedSegment?.is_dynamic,
    recentMembersReloadKey,
  ]);

  const loadSegments = async (): Promise<Segment[]> => {
    try {
      setLoading(true);
      // The Segments page is the one place that lists archived segments too
      // (shown on request, with Unarchive); every picker hides them.
      const data = await getSegments(undefined, { includeArchived: true });
      setSegments(data);
      return data;
    } catch (error) {
      console.error("Error loading segments:", error);
      showToast("error", "Error loading segments");
      return [];
    } finally {
      setLoading(false);
      setSegmentsLoadedOnce(true);
    }
  };

  const handleUnarchiveSegment = async (segment: Segment) => {
    setUnarchivingId(segment.id);
    try {
      const result = await unarchiveSegment(segment.id);
      setSegments((prev) =>
        prev.map((s) =>
          s.id === segment.id
            ? result.state === "live"
              ? { ...s, archived_at: null, archive_state: null }
              : { ...s, archive_state: "restoring" }
            : s,
        ),
      );
      showToast(
        "success",
        result.state === "live"
          ? `"${segment.name}" is active again.`
          : `Restoring "${segment.name}" from the archive. It reappears in the segment pickers when the members are back (usually within minutes).`,
      );
    } catch (error) {
      console.error("Error unarchiving segment:", error);
      showToast(
        "error",
        error instanceof Error
          ? error.message
          : "Could not unarchive the segment",
      );
    } finally {
      setUnarchivingId(null);
    }
  };

  const handleViewSegment = (segment: Segment) => {
    setSelectedSegment(segment);
    // The recent-members effect fires on selectedSegment.id change.
  };

  const handleRefreshSegment = async (segmentId: UUID) => {
    if (!canRefresh) {
      return;
    }
    setRefreshingId(segmentId);
    try {
      const result = await refreshListCount(segmentId);
      // Splice the new count + freshness back into local state. No need
      // to refetch the entire list.
      setSegments((prev) =>
        prev.map((s) =>
          s.id === segmentId
            ? {
                ...s,
                contact_count: result.contact_count,
                last_calculated: result.last_calculated,
              }
            : s,
        ),
      );
      if (selectedSegment?.id === segmentId) {
        setSelectedSegment((prev) =>
          prev
            ? {
                ...prev,
                contact_count: result.contact_count,
                last_calculated: result.last_calculated,
              }
            : prev,
        );
      }
    } catch (error) {
      console.error("Error refreshing segment count:", error);
      const message =
        error instanceof Error &&
        error.message.includes("CRITERIA_NOT_EVALUABLE")
          ? "Couldn't refresh — this segment uses unsupported criteria. Edit the segment and try again."
          : "Couldn't refresh segment count — see console for details.";
      showToast("error", message);
    } finally {
      setRefreshingId(null);
    }
  };

  const handleDownloadCsv = async () => {
    if (!selectedSegment) {
      return;
    }
    setDownloadingCsv(true);
    setCsvProgress({ loaded: 0, total: selectedSegment.contact_count ?? 0 });
    try {
      const result = await downloadSegmentCsv(selectedSegment.id, {
        onProgress: (loaded, total) => setCsvProgress({ loaded, total }),
        fields: exportFields.length > 0 ? exportFields : undefined,
      });
      if (result.truncated) {
        showToast(
          "info",
          `Downloaded ${result.rowCount.toLocaleString()} rows. The segment is larger; the export was capped to keep the browser responsive.`,
        );
      } else {
        showToast(
          "success",
          `Downloaded ${result.rowCount.toLocaleString()} contacts as ${result.filename}`,
        );
      }
    } catch (error) {
      // Full detail goes to the console for engineers; the operator gets something they can act on
      // and, when the server produced one, the correlation ID to quote to support. Telling an
      // operator to "see console for details" is not an error message.
      console.error("CSV download failed:", error);
      showToast("error", segmentCsvErrorMessage(error));
    } finally {
      setDownloadingCsv(false);
      setCsvProgress(null);
    }
  };

  const handleCreateSegment = async (
    input: CreateSegmentInput | UpdateSegmentInput,
  ) => {
    if (!user) {
      return;
    }

    await createSegment(input as CreateSegmentInput, user.id);
    await loadSegments();
    showToast("success", "Segment created successfully");
  };

  const handleUpdateSegment = async (
    input: CreateSegmentInput | UpdateSegmentInput,
  ) => {
    if (!user || !editingSegment) {
      return;
    }

    await updateSegment(
      editingSegment.id,
      input as UpdateSegmentInput,
      user.id,
    );
    // Use the returned data directly — `segments` state from closure
    // is the pre-update list, and React batches setSegments(...) so it
    // wouldn't be visible to a synchronous segments.find() here.
    const refreshed = await loadSegments();

    // Re-select the edited segment from the now-fresh list. The cached
    // count and (now-nulled, if criteria changed) last_calculated will
    // be reflected via the freshness indicator on the card + panel.
    if (selectedSegment?.id === editingSegment.id) {
      const next = refreshed.find((s) => s.id === editingSegment.id);
      if (next) {
        setSelectedSegment(next);
      }
    }

    showToast("success", "Segment updated successfully");
  };

  const handleDeleteSegment = async (id: string) => {
    if (!confirm("Are you sure you want to delete this segment?")) {
      return;
    }

    try {
      await deleteSegment(id);
      await loadSegments();
      if (selectedSegment?.id === id) {
        setSelectedSegment(null);
      }
      showToast("success", "Segment deleted successfully");
    } catch (error) {
      console.error("Error deleting segment:", error);
      showToast("error", "Error deleting segment");
    }
  };

  const handleAddContacts = async (contactIds: UUID[]) => {
    if (!selectedSegment || selectedSegment.is_dynamic) {
      return;
    }

    try {
      await addContactsToSegment(selectedSegment.id, contactIds, user?.id);
      // Refresh the count for this segment so the card + detail panel
      // reflect the addition. Static lists don't need the validator
      // gate so refresh always succeeds for eligible roles.
      if (canRefresh) {
        await handleRefreshSegment(selectedSegment.id);
      } else {
        await loadSegments();
      }
      // Refresh the "last 5 added" panel so the operator sees their
      // additions immediately. Bumping the key re-fires the race-safe
      // useEffect; no fire-and-forget call to maintain in this branch.
      setRecentMembersReloadKey((k) => k + 1);
      showToast(
        "success",
        `Added ${contactIds.length} contact${contactIds.length !== 1 ? "s" : ""} to segment`,
      );
    } catch (error) {
      console.error("Error adding contacts:", error);
      showToast("error", "Error adding contacts to segment");
      throw error;
    }
  };

  const handleImportSuccess = async (segmentId: string) => {
    const refreshed = await loadSegments();
    // Select the newly created segment
    const newSegment = refreshed.find((s) => s.id === segmentId);
    if (newSegment) {
      handleViewSegment(newSegment);
    }
  };

  const handleAppendSuccess = async (segmentId: string) => {
    const refreshed = await loadSegments();
    const segment = refreshed.find((s) => s.id === segmentId);
    if (segment) {
      handleViewSegment(segment);
    }
    setRecentMembersReloadKey((k) => k + 1);
    setImportHistoryKey((k) => k + 1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading segments...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl font-bold text-gray-900">Contact Segments</h1>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowImportWizard(true)}
              className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-600 transition-colors flex items-center gap-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                />
              </svg>
              Import CSV
            </button>
            <button
              onClick={() => setShowCombineModal(true)}
              className="px-4 py-2 border border-primary-600 text-primary-700 rounded hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-600 transition-colors"
            >
              Combine Segments
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-600 transition-colors"
            >
              Create Segment
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500">
          Select a segment to view contacts
        </p>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search segments..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full max-w-sm px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
        {archivedCount > 0 && (
          <label
            className="mt-2 flex items-center gap-2 text-sm text-gray-600"
            title="Segments that were not used for a while are archived: hidden from the segment pickers and cannot be sent to until you unarchive them."
          >
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="rounded border-gray-300"
            />
            Show archived segments ({archivedCount.toLocaleString()})
          </label>
        )}
      </div>

      <div
        className={`grid grid-cols-1 gap-6 ${selectedSegment ? "lg:grid-cols-3" : ""}`}
      >
        {/* Segments List */}
        <div className={selectedSegment ? "lg:col-span-2" : ""}>
          {/* Sort controls. Count + last_calculated are useful for ops
              workflows: "find the biggest segments" / "find segments that
              haven't been refreshed in a while". */}
          <div className="flex items-center gap-2 mb-3 text-sm text-gray-500 flex-wrap">
            <span>Sort by:</span>
            {(
              [
                { key: "name", label: "Name" },
                { key: "contact_count", label: "Size" },
                { key: "last_calculated", label: "Last calculated" },
                { key: "updated_at", label: "Updated" },
                { key: "created_at", label: "Created" },
              ] as Array<{ key: SegmentSortColumn; label: string }>
            ).map((opt) => (
              <button
                key={opt.key}
                onClick={() => toggleSort(opt.key)}
                className={`px-2 py-1 rounded ${
                  sortBy === opt.key
                    ? "bg-primary-100 text-primary-700 font-medium"
                    : "hover:bg-gray-100"
                }`}
              >
                {opt.label}{" "}
                {sortBy === opt.key &&
                  (sortOrder === "asc" ? "\u25B2" : "\u25BC")}
              </button>
            ))}
          </div>

          {filteredSegments.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
              {searchQuery
                ? "No segments match your search."
                : "No segments found. Create your first segment to get started."}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredSegments.map((segment) => (
                <div
                  key={segment.id}
                  className={`bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow ${
                    selectedSegment?.id === segment.id
                      ? "ring-2 ring-primary-500"
                      : ""
                  }`}
                  onClick={() => handleViewSegment(segment)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                          {segment.name}
                        </h3>
                        <span
                          className={`flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full ${
                            segment.is_dynamic
                              ? "bg-purple-100 text-purple-800"
                              : "bg-blue-100 text-blue-800"
                          }`}
                        >
                          {segment.is_dynamic ? "Dynamic" : "Static"}
                        </span>
                        {archiveLabel(segment) && (
                          <span
                            className="flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-200 text-gray-700"
                            title="Archived segments are hidden from the segment pickers and cannot be sent to. Unarchive to use it again."
                          >
                            {archiveLabel(segment)}
                          </span>
                        )}
                        {segment.list_type === "combined_snapshot" && (
                          <span
                            className={`flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full ${
                              segment.build_status === "ready"
                                ? "bg-green-100 text-green-800"
                                : segment.build_status === "failed"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-amber-100 text-amber-800"
                            }`}
                            title="A snapshot built from a combination of other segments"
                          >
                            {segment.build_status === "building"
                              ? "Combined · building…"
                              : segment.build_status === "failed"
                                ? "Combined · failed"
                                : "Combined"}
                          </span>
                        )}
                      </div>
                      <div className="mb-1">
                        <SegmentIdPill id={segment.id} />
                      </div>
                      {segment.description && (
                        <p className="text-sm text-gray-500 mb-2 truncate">
                          {segment.description}
                        </p>
                      )}
                      <div className="flex items-center gap-4 text-xs text-gray-400">
                        <span>
                          {(segment.contact_count ?? 0).toLocaleString()}{" "}
                          contacts
                        </span>
                        <span
                          className={
                            segment.last_calculated
                              ? "text-gray-400"
                              : "text-amber-600"
                          }
                          title={
                            segment.last_calculated
                              ? new Date(
                                  segment.last_calculated,
                                ).toLocaleString()
                              : "This segment has never been counted. Click Refresh to compute."
                          }
                        >
                          {formatLastCalculated(segment.last_calculated)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                      {segment.archived_at &&
                        canUnarchive &&
                        segment.archive_state !== "restoring" && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleUnarchiveSegment(segment);
                            }}
                            disabled={unarchivingId === segment.id}
                            className="px-3 py-1.5 text-xs font-medium text-gray-800 bg-amber-50 rounded hover:bg-amber-100 disabled:opacity-50 transition-colors"
                            title="Make this segment available again in the pickers (members are restored from the archive if they were moved there)"
                          >
                            {unarchivingId === segment.id
                              ? "Unarchiving…"
                              : "Unarchive"}
                          </button>
                        )}
                      {canRefresh && !segment.archived_at && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleRefreshSegment(segment.id);
                          }}
                          disabled={refreshingId === segment.id}
                          className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 rounded hover:bg-gray-100 disabled:opacity-50 transition-colors"
                          title="Recompute this segment's contact count"
                        >
                          {refreshingId === segment.id
                            ? "Refreshing…"
                            : "Refresh"}
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingSegment(segment);
                        }}
                        className="px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 rounded hover:bg-primary-100 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSegment(segment.id);
                        }}
                        className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Contact Details Panel (when a segment is selected) */}
        {selectedSegment && (
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6 sticky top-6">
              <div className="flex items-start justify-between mb-4">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900 truncate">
                    {selectedSegment.name}
                  </h2>
                  <div className="mt-0.5">
                    <SegmentIdPill id={selectedSegment.id} />
                  </div>
                </div>
                <button
                  onClick={() => setSelectedSegment(null)}
                  className="text-gray-400 hover:text-gray-600 ml-2 flex-shrink-0"
                >
                  ✕
                </button>
              </div>

              {selectedSegment.description && (
                <p className="text-sm text-gray-600 mb-4">
                  {selectedSegment.description}
                </p>
              )}

              <div className="mb-4 space-y-1">
                <p className="text-sm text-gray-500">
                  Type:{" "}
                  <span className="font-medium text-gray-900">
                    {selectedSegment.is_dynamic ? "Dynamic" : "Static"}
                  </span>
                </p>
                <p className="text-sm text-gray-500">
                  Contacts:{" "}
                  <span className="font-medium text-gray-900">
                    {(selectedSegment.contact_count ?? 0).toLocaleString()}
                  </span>{" "}
                  <span
                    className={`text-xs ${
                      selectedSegment.last_calculated
                        ? "text-gray-400"
                        : "text-amber-600"
                    }`}
                    title={
                      selectedSegment.last_calculated
                        ? `Last calculated ${new Date(
                            selectedSegment.last_calculated,
                          ).toLocaleString()}`
                        : "Click Refresh to compute"
                    }
                  >
                    ({formatLastCalculated(selectedSegment.last_calculated)})
                  </span>
                </p>
              </div>

              {/* Criteria summary — dynamic segments only. Read-only;
                  the operator already has Edit on the list card. */}
              {selectedSegment.is_dynamic && (
                <div className="border-t border-gray-200 pt-4 mb-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">
                    Criteria
                  </h3>
                  <p className="text-xs text-gray-700 break-words font-mono bg-gray-50 p-2 rounded">
                    {summariseCriteria(selectedSegment.query_criteria)}
                  </p>
                </div>
              )}

              {/* Last 5 added — static segments only. Operators use this
                  to confirm test accounts landed and to spot-check inbound
                  form/API traffic. Dynamic segments have no add-timestamp
                  so the panel is hidden. */}
              {!selectedSegment.is_dynamic && (
                <div className="border-t border-gray-200 pt-4 mb-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">
                    Last 5 added
                  </h3>
                  {recentMembersLoading ? (
                    <p className="text-xs text-gray-500 italic">Loading…</p>
                  ) : recentMembersError ? (
                    <p className="text-xs text-red-600">{recentMembersError}</p>
                  ) : recentMembers.length === 0 ? (
                    <p className="text-xs text-gray-500 italic">
                      No contacts in this segment yet.
                    </p>
                  ) : (
                    <ul className="text-xs space-y-2">
                      {recentMembers.map((m) => {
                        const fullName =
                          [m.first_name, m.last_name]
                            .filter(Boolean)
                            .join(" ") || null;
                        // Display name = name when we have one; otherwise
                        // email; otherwise placeholder. Email is shown on
                        // its own line below when both name and email
                        // exist (tester request 2026-05-12).
                        const primaryLabel = fullName ?? m.email ?? "(no name)";
                        const showEmailLine = Boolean(fullName && m.email);
                        return (
                          <li
                            key={m.contact_id}
                            className="flex items-start justify-between gap-2"
                          >
                            <div className="min-w-0 flex-1">
                              <Link
                                to={`/contacts/${m.contact_id}`}
                                className="text-primary-600 hover:text-primary-800 truncate block"
                                title={m.email ?? undefined}
                              >
                                {primaryLabel}
                              </Link>
                              {showEmailLine && (
                                <span className="text-gray-500 truncate block">
                                  {m.email}
                                </span>
                              )}
                            </div>
                            <span
                              className="text-gray-500 flex-shrink-0"
                              title={new Date(m.added_at).toLocaleString()}
                            >
                              {formatRelativeDateTime(m.added_at)}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}

              {/* Actions. Download CSV runs server-side count + paged
                  fetch — it computes its own total at fetch time and
                  doesn't depend on the cached contact_count being fresh
                  (or non-zero). Add Contacts opens its own search modal. */}
              <div className="border-t border-gray-200 pt-4 flex flex-wrap gap-2">
                <button
                  onClick={handleDownloadCsv}
                  disabled={downloadingCsv}
                  className="px-3 py-1.5 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 disabled:bg-gray-300"
                  title="Download all matching contacts as CSV"
                >
                  {downloadingCsv && csvProgress
                    ? `Downloading ${csvProgress.loaded.toLocaleString()} / ${csvProgress.total.toLocaleString()}…`
                    : "Download CSV"}
                </button>
                <button
                  onClick={() => setShowFieldPicker(true)}
                  disabled={downloadingCsv}
                  className="px-3 py-1.5 text-xs bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50"
                  title="Choose which contact fields to include in the CSV export"
                >
                  Fields ({exportFields.length})
                </button>
                {canRefresh && (
                  <button
                    onClick={() =>
                      void handleRefreshSegment(selectedSegment.id)
                    }
                    disabled={refreshingId === selectedSegment.id}
                    className="px-3 py-1.5 text-xs bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50"
                    title="Recompute this segment's contact count"
                  >
                    {refreshingId === selectedSegment.id
                      ? "Refreshing…"
                      : "Refresh count"}
                  </button>
                )}
                {!selectedSegment.is_dynamic &&
                  (selectedSegment.list_type ?? "static") === "static" &&
                  (selectedSegment.build_status === null ||
                    selectedSegment.build_status === undefined) && (
                    <>
                      <button
                        onClick={() => setShowAddContactsModal(true)}
                        className="px-3 py-1.5 text-xs bg-white border border-primary-600 text-primary-700 rounded hover:bg-primary-50"
                      >
                        + Add Contacts
                      </button>
                      <button
                        onClick={() => setShowAppendWizard(true)}
                        className="px-3 py-1.5 text-xs bg-white border border-primary-600 text-primary-700 rounded hover:bg-primary-50"
                      >
                        Append File
                      </button>
                    </>
                  )}
              </div>
            </div>
            {/* Import runs history + removals audit (code-audit M7) */}
            {!selectedSegment.is_dynamic && (
              <SegmentImportHistory
                key={`${selectedSegment.id}-${importHistoryKey}`}
                listId={selectedSegment.id}
              />
            )}
          </div>
        )}
      </div>

      {/* Combine Segments Modal (A1) — builds a static snapshot list from several segments. */}
      <CombineSegmentsModal
        isOpen={showCombineModal}
        onClose={() => setShowCombineModal(false)}
        onCreated={() => {
          setShowCombineModal(false);
          void loadSegments();
        }}
      />

      {/* Create Segment Modal */}
      <ListFormModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleCreateSegment}
        title="Create New Segment"
      />

      {/* Edit Segment Modal */}
      <ListFormModal
        isOpen={!!editingSegment}
        onClose={() => setEditingSegment(null)}
        onSave={handleUpdateSegment}
        list={editingSegment}
        title="Edit Segment"
      />

      {/* Add Contacts Modal (for static segments). The server-side add
          API is idempotent, so passing no exclusion set is safe — adding
          a contact that's already in the segment is a no-op. */}
      <ContactSearchModal
        isOpen={showAddContactsModal}
        onClose={() => setShowAddContactsModal(false)}
        onAdd={handleAddContacts}
        excludeContactIds={EMPTY_CONTACT_IDS}
        listName={selectedSegment?.name}
      />

      {/* Import Wizard */}
      <SegmentImportWizard
        isOpen={showImportWizard}
        onClose={() => setShowImportWizard(false)}
        onSuccess={handleImportSuccess}
      />

      <SegmentImportWizard
        isOpen={showAppendWizard}
        onClose={() => setShowAppendWizard(false)}
        onSuccess={handleAppendSuccess}
        mode="append"
        targetSegment={selectedSegment}
      />

      {/* CSV export field picker (Feature B). Selection persists in localStorage; CSV column order
          follows the order fields are checked. */}
      {showFieldPicker && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setShowFieldPicker(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">
                Export fields
              </h3>
              <span className="text-xs text-gray-500">
                {exportFields.length} selected
              </span>
            </div>
            <p className="px-4 pt-3 text-xs text-gray-500">
              Choose which contact fields to include. Columns appear in the CSV
              in the order you select them.
            </p>
            <div className="flex-1 overflow-y-auto px-4 py-3 grid grid-cols-2 gap-x-4 gap-y-1">
              {EXPORTABLE_FIELDS.map((f) => {
                const checked = exportFields.includes(f.key);
                const order = exportFields.indexOf(f.key) + 1;
                return (
                  <label
                    key={f.key}
                    className="flex items-center gap-2 text-xs text-gray-700 py-0.5 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleExportField(f.key)}
                      className="rounded border-gray-300"
                    />
                    <span>{f.label}</span>
                    {checked && (
                      <span className="ml-auto text-[10px] text-primary-600 font-medium">
                        {order}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex justify-between gap-2">
              <button
                onClick={() =>
                  persistExportFields([...DEFAULT_EXPORT_FIELD_KEYS])
                }
                className="px-3 py-1.5 text-xs bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
              >
                Reset to default
              </button>
              <button
                onClick={() => setShowFieldPicker(false)}
                className="px-3 py-1.5 text-xs bg-primary-600 text-white rounded hover:bg-primary-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
